import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Microsoft Teams host integration.
 *
 * The Teams app is a front end onto the same product: the tab loads the same
 * application, talks to the same API and holds a session for the same account,
 * so an evaluation opened in a meeting is the evaluation the browser shows.
 * Nothing here is required for the browser — outside Teams every call is a
 * no-op and the SDK is never even downloaded.
 */

export type TeamsFrame = 'content' | 'sidePanel' | 'meetingStage' | 'settings' | 'remove' | 'task' | 'unknown';

export type TeamsState = {
  /** `pending` until the host answers; the UI should not flash before that. */
  status: 'pending' | 'in-teams' | 'browser';
  frame: TeamsFrame;
  theme: 'default' | 'dark' | 'contrast';
  /** Present in a meeting side panel or meeting chat tab. */
  meetingId: string;
  chatId: string;
  channelId: string;
  teamName: string;
  userName: string;
  userEmail: string;
  /** The Entra tenant of the Teams user, not the workspace tenant. */
  entraTenantId: string;
  /** Set by the tab configuration page, read by the tab content. */
  projectId: string;
};

const EMPTY: TeamsState = {
  status: 'pending',
  frame: 'unknown',
  theme: 'default',
  meetingId: '',
  chatId: '',
  channelId: '',
  teamName: '',
  userName: '',
  userEmail: '',
  entraTenantId: '',
  projectId: '',
};

const TeamsContext = createContext<TeamsState>({ ...EMPTY, status: 'browser' });

/** Cheap pre-check so a normal visitor never downloads the Teams SDK. */
function looksLikeTeams(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('inTeams') || params.get('host') === 'teams') return true;
    const framed = window.self !== window.top || Boolean(window.opener);
    if (!framed) return false;
    const referrer = document.referrer || '';
    return (
      /teams\.microsoft\.(com|us)|teams\.live\.com|teams\.cloud\.microsoft|msteams/i.test(referrer)
      || /Teams\//i.test(navigator.userAgent)
      || /teams/i.test(window.name || '')
      // A framed page with no usable referrer may still be Teams desktop; the
      // SDK handshake below decides, and times out quickly if it is not.
      || referrer === ''
    );
  } catch {
    return false;
  }
}

type Sdk = typeof import('@microsoft/teams-js');
let sdkPromise: Promise<Sdk> | null = null;

export function loadTeamsSdk(): Promise<Sdk> {
  if (!sdkPromise) sdkPromise = import('@microsoft/teams-js');
  return sdkPromise;
}

function frameOf(raw: unknown): TeamsFrame {
  const value = String(raw || '').toLowerCase();
  if (value === 'sidepanel') return 'sidePanel';
  if (value === 'meetingstage') return 'meetingStage';
  if (value === 'settings') return 'settings';
  if (value === 'remove') return 'remove';
  if (value === 'task') return 'task';
  if (value === 'content') return 'content';
  return 'unknown';
}

export function TeamsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TeamsState>(() => ({
    ...EMPTY,
    status: typeof window === 'undefined' || !looksLikeTeams() ? 'browser' : 'pending',
  }));

  useEffect(() => {
    if (state.status !== 'pending') return;
    let cancelled = false;
    // The host answers in milliseconds when it is there. Outside Teams the
    // handshake never completes, so the wait is bounded rather than open.
    const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 2500));

    (async () => {
      try {
        const sdk = await loadTeamsSdk();
        const ready = sdk.app.initialize().then(() => sdk.app.getContext());
        const context = await Promise.race([ready, timeout]);
        if (cancelled) return;
        if (!context) {
          setState((current) => ({ ...current, status: 'browser' }));
          return;
        }
        const next: TeamsState = {
          status: 'in-teams',
          frame: frameOf(context.page?.frameContext),
          theme: (context.app?.theme as TeamsState['theme']) || 'default',
          meetingId: String(context.meeting?.id || ''),
          chatId: String(context.chat?.id || ''),
          channelId: String(context.channel?.id || ''),
          teamName: String(context.team?.displayName || ''),
          userName: String(context.user?.displayName || ''),
          userEmail: String(context.user?.userPrincipalName || context.user?.loginHint || ''),
          entraTenantId: String(context.user?.tenant?.id || ''),
          projectId: String(context.page?.subPageId || ''),
        };
        setState(next);
        sdk.app.notifySuccess();
        sdk.app.registerOnThemeChangeHandler((theme) => {
          setState((current) => ({ ...current, theme: (theme as TeamsState['theme']) || 'default' }));
        });
      } catch {
        if (!cancelled) setState((current) => ({ ...current, status: 'browser' }));
      }
    })();

    return () => { cancelled = true; };
  }, [state.status]);

  const value = useMemo(() => state, [state]);
  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
}

export function useTeams(): TeamsState {
  return useContext(TeamsContext);
}

/** True once the host has answered and we know we are inside Teams. */
export function useInTeams(): boolean {
  return useTeams().status === 'in-teams';
}

/**
 * Teams single sign-on: ask the host for an Entra token for this user. The
 * server exchanges it for a workspace session — see src/api/routes/teams.js.
 */
export async function requestSsoToken(): Promise<string> {
  const sdk = await loadTeamsSdk();
  return sdk.authentication.getAuthToken();
}

/**
 * The fallback that always works: a Teams popup running the product's own
 * sign-in page, which hands the session token back to the tab.
 */
export async function signInPopup(next?: string): Promise<string> {
  const sdk = await loadTeamsSdk();
  const url = new URL('/app/teams/signin', window.location.origin);
  url.searchParams.set('inTeams', '1');
  if (next) url.searchParams.set('next', next);
  const result = await sdk.authentication.authenticate({
    url: url.toString(),
    width: 520,
    height: 640,
  });
  return String(result || '');
}

/** Called by the popup page once a session exists. */
export async function completeSignInPopup(token: string): Promise<void> {
  const sdk = await loadTeamsSdk();
  sdk.authentication.notifySuccess(token);
}

export async function failSignInPopup(reason: string): Promise<void> {
  const sdk = await loadTeamsSdk();
  sdk.authentication.notifyFailure(reason);
}

/** Open a link in the browser rather than inside the tab. */
export async function openExternal(url: string): Promise<void> {
  try {
    const sdk = await loadTeamsSdk();
    await sdk.app.openLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** Put the current evaluation on the meeting stage for everyone to see. */
export async function shareToStage(url: string): Promise<void> {
  const sdk = await loadTeamsSdk();
  await new Promise<void>((resolve, reject) => {
    sdk.meeting.shareAppContentToStage((error) => {
      if (error) reject(new Error(error.message || 'Could not share to the stage'));
      else resolve();
    }, url);
  });
}

export async function canShareToStage(): Promise<boolean> {
  try {
    const sdk = await loadTeamsSdk();
    return await new Promise<boolean>((resolve) => {
      sdk.meeting.getAppContentStageSharingCapabilities((error, capabilities) => {
        resolve(!error && Boolean(capabilities?.doesAppHaveSharePermission));
      });
    });
  } catch {
    return false;
  }
}

/** A link that opens this evaluation in the Teams tab on any device. */
export function teamsDeepLink(appId: string, entityId: string, subPageId?: string): string {
  const context = subPageId ? `&context=${encodeURIComponent(JSON.stringify({ subEntityId: subPageId }))}` : '';
  return `https://teams.microsoft.com/l/entity/${appId}/${encodeURIComponent(entityId)}?label=Hanseatic%20Select${context}`;
}
